import { useState, useEffect, useCallback, useMemo } from "react";
import { Input, Tag, Typography, Space, Tooltip, Pagination } from "antd";
import {
  TbGripVertical,
  TbAlertTriangle,
  TbCircleCheck,
  TbClockHour4,
  TbSearch,
  TbMail,
  TbBrandGoogle,
  TbBrandWindows,
  TbServer,
  TbCalendarTime,
} from "react-icons/tb";
import { ApiService } from "api/api.service";

const { Text } = Typography;

export enum ProfileType {
  DEFAULT = "default",
  GOOGLE = "google",
  MICROSOFT = "microsoft",
  YAHOO = "yahoo",
  APPLE = "apple",
  SMTP = "smtp_password",
}

export type ProfileDto = {
  email: string;
  emailVerified: boolean;
  user: string;
  type: ProfileType;
  isActive: boolean;
  isLastUsed: boolean;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  avatarFile?: string;
};

export type SyncJobDto = {
  profile: ProfileDto;
  syncTill?: Date;
  nextSyncAt?: Date;
  errors?: number;
};

export type T_Api_Paginated<T> = {
  data: T[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  nextPage: null | number;
  prevPage: null | number;
  limit: number;
  page: number;
  totalObjects: number;
  totalPages: number;
};

const profileTypeConfig: Record<ProfileType, { label: string; color: string; icon: React.ReactNode }> = {
  [ProfileType.GOOGLE]: { label: "Google", color: "#4285F4", icon: <TbBrandGoogle size={12} /> },
  [ProfileType.MICROSOFT]: { label: "Microsoft", color: "#00A4EF", icon: <TbBrandWindows size={12} /> },
  [ProfileType.YAHOO]: { label: "Yahoo", color: "#6001D2", icon: <TbMail size={12} /> },
  [ProfileType.APPLE]: { label: "Apple", color: "#555", icon: <TbMail size={12} /> },
  [ProfileType.SMTP]: { label: "SMTP", color: "#E85D04", icon: <TbServer size={12} /> },
  [ProfileType.DEFAULT]: { label: "Default", color: "#6366F1", icon: <TbMail size={12} /> },
};

function formatRelative(date?: Date | string): { label: string; urgency: "overdue" | "soon" | "upcoming" | "none" } {
  if (!date) return { label: "—", urgency: "none" };
  const diff = new Date(date).getTime() - Date.now();
  const abs = Math.abs(diff);
  const mins = Math.floor(abs / 60000);
  const hours = Math.floor(abs / 3600000);
  const days = Math.floor(abs / 86400000);
  const past = diff < 0;

  let label: string;
  if (mins < 1) label = past ? "just now" : "in moments";
  else if (hours < 1) label = past ? `${mins}m ago` : `in ${mins}m`;
  else if (days < 1) label = past ? `${hours}h ago` : `in ${hours}h`;
  else label = past ? `${days}d ago` : `in ${days}d`;

  let urgency: "overdue" | "soon" | "upcoming" | "none";
  if (past) urgency = "overdue";
  else if (hours < 2) urgency = "soon";
  else if (days < 1) urgency = "upcoming";
  else urgency = "none";

  return { label, urgency };
}

const urgencyStyle: Record<string, { bg: string; border: string; text: string; dot: string }> = {
  overdue: { bg: "#FEF2F2", border: "#FECACA", text: "#B91C1C", dot: "#EF4444" },
  soon: { bg: "#FFFBEB", border: "#FDE68A", text: "#92400E", dot: "#F59E0B" },
  upcoming: { bg: "#EEF2FF", border: "#C7D2FE", text: "#3730A3", dot: "#6366F1" },
  none: { bg: "#F9FAFB", border: "#E5E7EB", text: "#6B7280", dot: "#9CA3AF" },
};

type DragState = { dragIndex: number; overIndex: number } | null;

function NextSyncBadge({ date }: { date?: Date | string }) {
  const { label, urgency } = formatRelative(date);
  const s = urgencyStyle[urgency];
  return (
    <Tooltip title={date ? new Date(date).toLocaleString() : "Not scheduled"}>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 10px",
          borderRadius: 20,
          background: s.bg,
          border: `1px solid ${s.border}`,
          flexShrink: 0,
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
        <TbClockHour4 size={12} style={{ color: s.text }} />
        <Text style={{ fontSize: 11, fontWeight: 600, color: s.text, whiteSpace: "nowrap" }}>{label}</Text>
      </div>
    </Tooltip>
  );
}

function SyncJobRow({
  job,
  index,
  dragState,
  onDragStart,
  onDragEnter,
  onDragEnd,
}: Readonly<{
  job: SyncJobDto;
  index: number;
  dragState: DragState;
  onDragStart: (i: number) => void;
  onDragEnter: (i: number) => void;
  onDragEnd: () => void;
}>) {
  const { profile } = job;
  const cfg = profileTypeConfig[profile.type];
  const errorCount = job.errors ?? 0;
  const isDragging = dragState?.dragIndex === index;
  const isOver = dragState?.overIndex === index && dragState?.dragIndex !== index;

  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragEnter={() => onDragEnter(index)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => e.preventDefault()}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        borderRadius: 8,
        border: isOver ? "1.5px dashed #6366F1" : "1px solid #EBEBEB",
        background: isDragging ? "#F5F3FF" : isOver ? "#EEF2FF" : "#fff",
        opacity: isDragging ? 0.45 : 1,
        cursor: "grab",
        transition: "border-color 0.15s, background 0.15s",
        userSelect: "none",
      }}
    >
      <TbGripVertical size={16} style={{ color: "#D1D5DB", flexShrink: 0 }} />

      <Text style={{ flex: 1, minWidth: 0, fontSize: 13, display: "block", lineHeight: "1.3", color: "#111827" }}>
        {profile.email}
      </Text>

      <Tag
        style={{
          background: cfg.color + "14",
          borderColor: cfg.color + "35",
          color: cfg.color,
          fontWeight: 500,
          fontSize: 11,
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          padding: "1px 7px",
          flexShrink: 0,
          margin: 0,
        }}
      >
        {cfg.icon}
        {cfg.label}
      </Tag>

      <div style={{ width: 1, height: 24, background: "#EBEBEB", flexShrink: 0 }} />

      <Tooltip title={job.syncTill ? new Date(job.syncTill).toLocaleString() : "Not set"}>
        <Space size={4} style={{ flexShrink: 0, minWidth: 96 }}>
          <TbCalendarTime size={13} style={{ color: "#9CA3AF" }} />
          <Text style={{ fontSize: 11, color: "#9CA3AF" }}>
            {job.syncTill ? (
              new Date(job.syncTill).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
            ) : (
              <span style={{ color: "#D1D5DB" }}>—</span>
            )}
          </Text>
        </Space>
      </Tooltip>

      <NextSyncBadge date={job.nextSyncAt} />

      <div style={{ flexShrink: 0, width: 44, display: "flex", justifyContent: "center" }}>
        {errorCount === 0 ? (
          <TbCircleCheck size={15} style={{ color: "#16A34A" }} />
        ) : (
          <Space size={3}>
            <TbAlertTriangle size={13} style={{ color: "#DC2626" }} />
            <Text style={{ fontSize: 11, color: "#DC2626", fontWeight: 600 }}>{errorCount}</Text>
          </Space>
        )}
      </div>
    </div>
  );
}

export default function SyncJobsPage() {
  const [order, setOrder] = useState<SyncJobDto[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [dragState, setDragState] = useState<DragState>(null);
  const syncQueue = ApiService.email.useGetSyncQueue({ page, limit, search });
  const data = syncQueue.data?.result.data ?? [];

  useEffect(() => {
    if (syncQueue.data?.result.data) setOrder(syncQueue.data.result.data);
  }, [syncQueue.data?.result.data]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleDragStart = useCallback((index: number) => {
    setDragState({ dragIndex: index, overIndex: index });
  }, []);

  const handleDragEnter = useCallback((overIndex: number) => {
    setDragState((prev) => (prev ? { ...prev, overIndex } : null));
  }, []);

  const handleDragEnd = useCallback(() => {
    if (!dragState || dragState.dragIndex === dragState.overIndex) {
      setDragState(null);
      return;
    }
    const { dragIndex, overIndex } = dragState;
    const reordered = [...data];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(overIndex, 0, moved);

    const pageStart = (page - 1) * limit;
    const newOrder = [...order];
    reordered.forEach((job, i) => {
      newOrder[pageStart + i] = job;
    });
    setOrder(newOrder);
    setDragState(null);
  }, [dragState, data, page, order]);

  const displayItems = useMemo(() => {
    if (!dragState) return data;
    const items = [...data];
    const [moved] = items.splice(dragState.dragIndex, 1);
    items.splice(dragState.overIndex, 0, moved);
    return items;
  }, [dragState, data]);

  return (
    <div style={{ padding: "24px 28px", minHeight: "100vh", background: "#FAFAFA" }}>
      <Input
        prefix={<TbSearch size={14} style={{ color: "#9CA3AF" }} />}
        placeholder="Search by email…"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        allowClear
        className="w-80"
        style={{ marginBottom: 16, fontSize: 13 }}
      />

      <div style={{ display: "flex", flexDirection: "column", gap: 6, minHeight: 200 }}>
        {displayItems.map((job, index) => (
          <SyncJobRow
            key={job.profile.email}
            job={job}
            index={index}
            dragState={dragState}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragEnd={handleDragEnd}
          />
        ))}
        {displayItems.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              No sync jobs found
            </Text>
          </div>
        )}
      </div>

      {(syncQueue.data?.result.totalPages ?? 0) > 1 && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <Pagination
            current={syncQueue.data?.result.page}
            pageSize={limit}
            total={syncQueue.data?.result.totalObjects}
            onChange={(p) => setPage(p)}
            showSizeChanger={false}
            size="small"
            showTotal={(total, range) => (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {range[0]}–{range[1]} of {total}
              </Text>
            )}
          />
        </div>
      )}
    </div>
  );
}
